import { Button, IconButton } from "@material-ui/core";
import ChevronRightRoundedIcon from "@material-ui/icons/ChevronRightRounded";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import ActionIcon from "@material-ui/icons/MoreHoriz";
import FilterNoneOutlinedIcon from "@material-ui/icons/FilterNoneOutlined";
import { useIsDesktop } from "@my-scoot/exly-react-component-lib";
import ExlyPopover from "common/Components/ExlyPopover";
import { IExlyPopover } from "common/Components/ExlyPopover/interfaces/IExlyPopover";
import { isPast } from "date-fns";
import React, { FC } from "react";
import { orgPermissions, RESOURCE_KEYS } from "utils/OrgPermissions";
import ExpiredStatus from "../StatusField/components/ExpiredStatus/ExpiredStatus";
import { IActions } from "./interfaces/IActions";
import { LINK_TYPES } from "features/PaymentLinks/modules/CreatePaymentLinkModal/constants/CreatePaymentLinkModal.constants";
import { checkIsCheckoutEnabledListing } from "features/CheckoutPages/utils/checkIsCheckoutEnabledListing";
import styles from "./Actions.module.css";
import { PAYMENT_LINK_ACTION_IDS } from "./Actions.constants";
import { canShowQr, noop } from "./Actions.utils";
import ExlyImage from "features/Common/modules/ExlyImage/ExlyImage";
import { getS3AssetCDNUrl } from "features/Common/modules/File/modules/Upload/utils/FileUpload.utils";

const QrIcon = () => (
  <ExlyImage
    src={getS3AssetCDNUrl("assets/qr_code.svg")}
    alt="QR Code"
    width={18}
    height={18}
  />
);

const Actions: FC<IActions> = ({
  record,
  onEditCheckout = noop,
  onEdit = noop,
  onExpire = noop,
  actionKey = "Link",
  onEditThankYouPage = noop,
  onDuplicate = noop,
  onShowQr = noop,
}) => {
  const isDesktop = useIsDesktop();

  let isExpired = !!record.expiry_date && isPast(new Date(record.expiry_date));

  if (isExpired) return isDesktop ? <ExpiredStatus /> : null;

  const hasWriteAccess = orgPermissions.hasFeatureWriteAccess(
    RESOURCE_KEYS.SECTIONS.PAYMENT_LINKS
  );

  if (!hasWriteAccess) return null;

  const ActionCta: IExlyPopover["AnchorComponent"] = ({ openPopover }) => {
    return isDesktop ? (
      <IconButton onClick={openPopover}>
        <ActionIcon />
      </IconButton>
    ) : (
      <div className={styles.action} onClick={openPopover}>
        Actions <ChevronRightRoundedIcon />
      </div>
    );
  };

  const PopoverComponent: IExlyPopover["PopoverComponent"] = ({
    closePopover,
  }) => {
    const formActions: Array<{
      id: number;
      label: string;
      icon: React.ElementType;
      onClick?: (record: any) => void;
      hide?: boolean;
    }> = [
      {
        id: PAYMENT_LINK_ACTION_IDS.SETTINGS,
        label: `${actionKey} Settings`,
        icon: SettingsOutlinedIcon,
        onClick: onEdit,
        hide: false,
      },
      {
        id: PAYMENT_LINK_ACTION_IDS.EDIT_CHECKOUT,
        label: `Edit ${actionKey}`,
        icon: EditIcon,
        onClick: onEditCheckout,
        hide: record?.is_combo
          ? false
          : record?.payment_link_type !== LINK_TYPES.CUSTOM_LINK ||
            !checkIsCheckoutEnabledListing({ type: record?.listing_type }),
      },
      {
        id: PAYMENT_LINK_ACTION_IDS.EDIT_THANK_YOU,
        label: `Edit Thank you Page`,
        icon: EditIcon,
        onClick: onEditThankYouPage,
      },
      {
        id: PAYMENT_LINK_ACTION_IDS.SHOW_QR,
        label: "Show QR Payment link",
        icon: QrIcon,
        onClick: onShowQr,
        hide: !canShowQr(record, onShowQr),
      },
      {
        id: PAYMENT_LINK_ACTION_IDS.EXPIRE,
        label: `Expire ${actionKey}`,
        icon: DeleteIcon,
        onClick: onExpire,
        hide: false,
      },
      {
        id: PAYMENT_LINK_ACTION_IDS.DUPLICATE,
        label: `Duplicate ${actionKey}`,
        icon: FilterNoneOutlinedIcon,
        onClick: onDuplicate,
        hide: !(record?.is_combo ?? false),
      },
    ];

    return (
      <div>
        {formActions.map(({ id, label, icon: Icon, onClick, hide }) => {
          if (hide) {
            return <></>;
          }
          return (
            <div key={id}>
              <Button
                className={styles.actionCta}
                fullWidth
                startIcon={<Icon />}
                disabled={!hasWriteAccess}
                onClick={() => {
                  if (onClick) {
                    onClick(record);
                  }
                  closePopover();
                }}
              >
                {label}
              </Button>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <ExlyPopover
      AnchorComponent={ActionCta}
      PopoverComponent={PopoverComponent}
      forceDesktopDesign={false}
      popoverProps={{
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "left",
        },
        transformOrigin: {
          vertical: "top",
          horizontal: "right",
        },
      }}
    />
  );
};

export default Actions;
